import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import GoogleButton from '../components/GoogleButton';
import './Login.css';

// หน้าที่จะพาไปหลังเข้าสู่ระบบ — รับได้จาก state.from (redirect ใน App.js) หรือ ?next= (api.js ตอน token หมดอายุ)
// ยอมรับเฉพาะ path ภายในเว็บ (ขึ้นต้นด้วย "/" ตัวเดียว) กัน open-redirect ออกนอกโดเมน
function resolveNextPath(location) {
    const candidates = [
        location.state?.from,
        new URLSearchParams(location.search).get('next')
    ];
    for (const c of candidates) {
        if (typeof c !== 'string') continue;
        if (!c.startsWith('/') || c.startsWith('//') || c.startsWith('/login')) continue;
        return c;
    }
    return '/';
}

function Login() {
    const [cid, setCid] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(false);
    const [require2fa, setRequire2fa] = useState(false);
    const [totpCode, setTotpCode] = useState('');
    const [googleCred, setGoogleCred] = useState(null); // เก็บ credential ไว้ resubmit ตอนกรอก 2FA
    const [googleOn, setGoogleOn] = useState(true);      // ซ่อนบล็อก Google ถ้ายังไม่ได้ตั้งค่า Client ID
    const [pidCode, setPidCode] = useState(null);        // รหัสใช้ครั้งเดียวจาก callback Provider ID (เก็บไว้ resubmit ตอนกรอก 2FA)
    const [pidBusy, setPidBusy] = useState(false);       // กำลังแลกรหัส Provider ID เป็น token
    const pidHandledRef = useRef(false);
    const { login, loginWithGoogle, loginWithProviderId, user } = useAuth();
    const { appName, providerIdEnabled } = useBranding();
    const navigate = useNavigate();
    const location = useLocation();
    const nextPath = resolveNextPath(location);

    useEffect(() => {
        if (user) {
            navigate(nextPath, { replace: true });
        }
    }, [user, navigate, nextPath]);

    // กลับมาจาก Provider ID: server พามาที่ /login?pid_code=... (สำเร็จ) หรือ ?pid_error=... (ถูกปฏิเสธ)
    // แลกรหัสครั้งเดียวเป็น token แล้วล้าง query ออกจาก URL กันแลกซ้ำตอน re-render
    useEffect(() => {
        if (pidHandledRef.current) return;
        const params = new URLSearchParams(location.search);
        const code = params.get('pid_code');
        const pidError = params.get('pid_error');
        if (!code && !pidError) return;
        pidHandledRef.current = true;

        params.delete('pid_code');
        params.delete('pid_error');
        const rest = params.toString();
        navigate(`/login${rest ? `?${rest}` : ''}`, { replace: true, state: location.state });

        if (pidError) {
            setError(pidError);
            setNotFound(pidError.includes('ไม่พบ'));
            return;
        }

        (async () => {
            setPidBusy(true);
            setError('');
            try {
                const result = await loginWithProviderId(code);
                if (result?.require2fa) {
                    setPidCode(code);
                    setRequire2fa(true);
                    return;
                }
                // fetchUser ใน context จะตั้ง user → useEffect ด้านบนพาไป nextPath
            } catch (err) {
                const data = err.response?.data || {};
                setError(data.error || 'เข้าสู่ระบบด้วย Provider ID ไม่สำเร็จ');
                if (data.require2fa) { setPidCode(code); setRequire2fa(true); }
            } finally {
                setPidBusy(false);
            }
        })();
    }, [location.search, location.state, navigate, loginWithProviderId]);

    const startProviderIdLogin = () => {
        // เริ่ม OAuth ที่ server (มี client secret) — เป็น full-page navigation ไป moph.id.th
        const next = nextPath && nextPath !== '/' ? `?next=${encodeURIComponent(nextPath)}` : '';
        window.location.href = `/api/auth/providerid/start${next}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setNotFound(false);
        setLoading(true);

        try {
            // ถ้าอยู่ในขั้น 2FA ของ Google / Provider ID ให้ resubmit ด้วย credential เดิม
            let result;
            if (require2fa && pidCode) {
                result = await loginWithProviderId(pidCode, totpCode);
            } else if (require2fa && googleCred) {
                result = await loginWithGoogle(googleCred, totpCode);
            } else {
                result = await login(cid, password, require2fa ? totpCode : undefined);
            }
            if (result?.require2fa) {
                // ขั้นแรก: รหัสผ่านถูก แต่ต้องกรอกรหัส 2FA
                setRequire2fa(true);
                setError('');
                setLoading(false);
                return;
            }
            navigate(nextPath, { replace: true });
        } catch (err) {
            const data = err.response?.data || {};
            const msg = data.error || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ';
            setError(msg);
            if (data.require2fa) {
                // รหัส 2FA ผิด — ให้กรอกใหม่
                setRequire2fa(true);
            } else {
                setNotFound(msg.includes('ไม่พบ'));
            }
        } finally {
            setLoading(false);
        }
    };

    // เข้าสู่ระบบด้วย Google — ได้ ID token แล้วส่งให้ backend
    const handleGoogleCredential = useCallback(async (credential) => {
        setError(''); setNotFound(false); setLoading(true);
        try {
            const result = await loginWithGoogle(credential);
            if (result?.require2fa) {
                setGoogleCred(credential); // จำไว้ resubmit พร้อม 2FA
                setRequire2fa(true);
                setLoading(false);
                return;
            }
            navigate(nextPath, { replace: true });
        } catch (err) {
            const data = err.response?.data || {};
            setError(data.error || 'เข้าสู่ระบบด้วย Google ไม่สำเร็จ');
            if (data.require2fa) { setGoogleCred(credential); setRequire2fa(true); }
            setLoading(false);
        }
    }, [loginWithGoogle, navigate, nextPath]);

    const cancel2fa = () => {
        setRequire2fa(false);
        setTotpCode('');
        setError('');
        setGoogleCred(null);
        setPidCode(null);
    };

    const handleInputChange = (value) => {
        // ถ้าเป็นตัวเลขล้วน ให้จำกัดที่ 13 หลัก (CID)
        // ถ้ามีตัวอักษร ให้เป็น username ได้
        if (/^\d*$/.test(value)) {
            return value.slice(0, 13);
        }
        return value.slice(0, 50);
    };

    return (
        <div className="login-page">
            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <div className="login-logo">
                            <img
                                src={`${process.env.PUBLIC_URL}/icon-512.png`}
                                alt={appName}
                                className="login-logo-icon"
                            />
                            <h1 className="login-title">{appName}</h1>
                        </div>
                        <p className="login-subtitle">ระบบดูข้อมูลเงินเดือน</p>
                        <p className="login-hospital">โรงพยาบาลศรีสะเกษ</p>
                    </div>

                    <form onSubmit={handleSubmit} className="login-form">
                        {error && (
                            <div className="alert alert-error">
                                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                                </svg>
                                {error}
                            </div>
                        )}

                        {notFound && (
                            <div className="login-register-cta">
                                <span>ยังไม่มีบัญชีในระบบ?</span>
                                <Link to="/register" className="steam-btn steam-btn-secondary">
                                    ลงทะเบียนขอเปิดบัญชี
                                </Link>
                                <Link to="/check-status" className="register-link" style={{ fontSize: 13 }}>
                                    เคยขอเปิดบัญชีแล้ว? ตรวจสอบสถานะ / เพิ่มอีเมลที่นี่
                                </Link>
                            </div>
                        )}

                        {!require2fa ? (
                            <>
                                <div className="form-group">
                                    <label className="form-label">เลขบัตรประชาชน / Username</label>
                                    <input
                                        type="text"
                                        className="steam-input"
                                        value={cid}
                                        onChange={(e) => setCid(handleInputChange(e.target.value))}
                                        placeholder="กรอกเลขบัตรประชาชน 13 หลัก หรือ Username"
                                        maxLength="50"
                                        required
                                        autoFocus
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">รหัสผ่าน</label>
                                    <input
                                        type="password"
                                        className="steam-input"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="กรอกรหัสผ่าน"
                                        required
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="form-group">
                                <label className="form-label">รหัสยืนยัน 2 ชั้น (จากแอป Authenticator)</label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className="steam-input"
                                    style={{ letterSpacing: '6px', textAlign: 'center', fontSize: 20 }}
                                    value={totpCode}
                                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    placeholder="______"
                                    maxLength="6"
                                    autoFocus
                                />
                                <p style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                                    เปิดแอป Google / Microsoft Authenticator แล้วกรอกรหัส 6 หลักของ iMoney
                                </p>
                            </div>
                        )}

                        <button
                            type="submit"
                            className="steam-btn steam-btn-primary login-btn"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }}></div>
                                    {require2fa ? 'กำลังยืนยัน...' : 'กำลังเข้าสู่ระบบ...'}
                                </>
                            ) : require2fa ? (
                                'ยืนยันรหัส'
                            ) : (
                                <>
                                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                                        <polyline points="10,17 15,12 10,7"/>
                                        <line x1="15" y1="12" x2="3" y2="12"/>
                                    </svg>
                                    เข้าสู่ระบบ
                                </>
                            )}
                        </button>

                        {require2fa && (
                            <button
                                type="button"
                                className="steam-btn steam-btn-secondary login-btn"
                                style={{ marginTop: 8 }}
                                onClick={cancel2fa}
                            >
                                ย้อนกลับ
                            </button>
                        )}
                    </form>

                    {!require2fa && (providerIdEnabled || googleOn) && (
                        <div className="login-google-block">
                            <div className="login-divider"><span>หรือ</span></div>

                            {providerIdEnabled && (
                                <>
                                    <button
                                        type="button"
                                        className="btn-providerid"
                                        onClick={startProviderIdLogin}
                                        disabled={pidBusy || loading}
                                    >
                                        {pidBusy ? (
                                            <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }}></div>
                                        ) : (
                                            <img src={`${process.env.PUBLIC_URL}/providerid.png`} alt="Provider ID" />
                                        )}
                                        <span>{pidBusy ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบด้วย Provider ID'}</span>
                                    </button>
                                    <p className="login-providerid-note">
                                        ยืนยันตัวตนผ่าน Health ID กระทรวงสาธารณสุข — ใช้ได้กับบุคลากรที่มีเลขบัตรในฐานเงินเดือน รพ.ศรีสะเกษ
                                    </p>
                                </>
                            )}

                            {googleOn && (
                                <>
                                    <div className="login-google">
                                        <GoogleButton
                                            text="signin_with"
                                            onCredential={handleGoogleCredential}
                                            onUnavailable={() => setGoogleOn(false)}
                                        />
                                    </div>
                                    <p className="login-google-note">
                                        ใช้ได้เฉพาะบัญชีที่ผูกอีเมล Google ไว้แล้ว (ผูกได้ในหน้าโปรไฟล์)
                                    </p>
                                </>
                            )}
                        </div>
                    )}

                    <div className="login-footer">
                        <p>ใช้เลขบัตรประชาชนและรหัสผ่านเดียวกับระบบ Slip Online</p>
                        <p style={{ fontSize: '12px', marginTop: '4px', color: '#9ca3af' }}>Admin: ใช้ Username ในการเข้าสู่ระบบ</p>
                        <p style={{ marginTop: '10px' }}>
                            ยังไม่มีบัญชี? <Link to="/register" className="register-link">ลงทะเบียนขอเปิดบัญชี</Link>
                        </p>
                        <p style={{ marginTop: '4px' }}>
                            <Link to="/check-status" className="register-link">ตรวจสอบสถานะ / เพิ่มอีเมลคำขอเปิดบัญชี</Link>
                        </p>
                        <p style={{ marginTop: '4px' }}>
                            <Link to="/reset-password" className="register-link">ลืมรหัสผ่าน? ขอรีเซ็ตรหัสผ่าน</Link>
                        </p>
                    </div>
                </div>

                <div className="login-info">
                    <div className="info-card">
                        <h3>ดูสลิปเงินเดือน</h3>
                        <p>ดูรายละเอียดเงินเดือนของคุณได้ทุกที่ทุกเวลา</p>
                    </div>
                    <div className="info-card">
                        <h3>พิมพ์เอกสาร</h3>
                        <p>พิมพ์สลิปเงินเดือนและหนังสือ 50 ทวิ ในรูปแบบ PDF</p>
                    </div>
                    <div className="info-card">
                        <h3>รองรับทุกอุปกรณ์</h3>
                        <p>ใช้งานได้ทั้ง Computer, Tablet และ Mobile</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Login;
